import { escapeHtml, escapeHtmlAttr } from '../utils/html';
import type { RdRow } from './reset-device-types';
import {
  rdRows, rdSearch, rdPage, rdPageSize, rdSortCol, rdSortAsc, rdSelectedId,
  rdExcludedNonGeotab,
} from './reset-device-page';

// ── Page state machine ────────────────────────────────────────────────────
// Four sibling blocks, exactly one visible. Mirrors watchdog-results.ts:39-53.

export function showRdState(s: 'empty' | 'loading' | 'error' | 'results', msg = ''): void {
  document.getElementById('rdEmpty')!  .classList.toggle('hidden', s !== 'empty');
  document.getElementById('rdLoading')!.classList.toggle('hidden', s !== 'loading');
  document.getElementById('rdErrBox')! .classList.toggle('hidden', s !== 'error');
  document.getElementById('rdResults')!.classList.toggle('hidden', s !== 'results');

  if (s === 'loading' && msg) {
    const label = document.getElementById('rdLoadingLabel');
    if (label) label.textContent = msg;
  }
  if (s === 'error') {
    const box = document.getElementById('rdErrBox');
    if (box) box.textContent = msg;
  }
}

// ── Sorting ───────────────────────────────────────────────────────────────

const SORT_VALUE: Record<string, (r: RdRow) => string> = {
  name:         (r) => r.name.toLowerCase(),
  serialNumber: (r) => r.serialNumber.toLowerCase(),
  vin:          (r) => r.vin.toLowerCase(),
  deviceType:   (r) => r.deviceType.toLowerCase(),
  groupNames:   (r) => r.groupNames.toLowerCase(),
};

const COLS: { key: string; label: string }[] = [
  { key: 'name',         label: 'Device Name' },
  { key: 'serialNumber', label: 'Serial Number' },
  { key: 'vin',          label: 'VIN' },
  { key: 'deviceType',   label: 'Type' },
  { key: 'groupNames',   label: 'Groups' },
];

/** Rows after the current search term, in current sort order. */
export function rdFilteredSorted(): RdRow[] {
  const term = rdSearch.toLowerCase().trim();
  const filtered = rdRows.filter((r) => {
    if (!term) return true;
    return r.name.toLowerCase().includes(term)
        || r.serialNumber.toLowerCase().includes(term)
        || r.vin.toLowerCase().includes(term);
  });

  const valOf = SORT_VALUE[rdSortCol] ?? SORT_VALUE.name;
  const dir = rdSortAsc ? 1 : -1;
  return [...filtered].sort((a, b) => {
    const av = valOf(a), bv = valOf(b);
    if (av < bv) return -dir;
    if (av > bv) return dir;
    return 0;
  });
}

// ── Render ────────────────────────────────────────────────────────────────

export function renderRdResults(): void {
  const sorted = rdFilteredSorted();

  const pageSize    = rdPageSize > 0 ? rdPageSize : sorted.length || 1;
  const totalPages  = Math.ceil(sorted.length / pageSize) || 1;
  const currentPage = Math.min(rdPage, totalPages - 1);
  const pageStart   = currentPage * pageSize;
  const pageRows    = sorted.slice(pageStart, pageStart + pageSize);

  // Sort control is a real <button> inside the <th>: a bare <th onclick> is not
  // focusable, so the existing tables in this app are mouse-only. aria-sort goes
  // on the th, which is where assistive tech looks for it.
  const thHtml = '<th class="rd-th-pick"><span class="rd-sr-only">Select</span></th>'
    + COLS.map((col) => {
      const active = col.key === rdSortCol;
      const arrow  = active ? (rdSortAsc ? '▲' : '▼') : '▲';
      const sort   = active ? (rdSortAsc ? 'ascending' : 'descending') : 'none';
      return `<th class="${active ? 'sort-active' : ''}" aria-sort="${sort}">`
           + `<button type="button" class="rd-th-btn" data-sort="${escapeHtmlAttr(col.key)}">`
           + `${escapeHtml(col.label)}<span class="sort-arrow ${active ? '' : 'sort-arrow-dim'}">${arrow}</span>`
           + `</button></th>`;
    }).join('');

  // A native radio rather than a styled span: it is focusable, announced, and
  // arrow-navigable within the group for free, and it matches this codebase's
  // convention of using real inputs (safety-page.ts:364, idling-page.ts:558).
  // Clicking anywhere on the row also selects it.
  const rowsHtml = pageRows.map((r) => {
    const picked = r.id === rdSelectedId;
    return `<tr class="rd-row${picked ? ' rd-row-picked' : ''}" data-pick="${escapeHtmlAttr(r.id)}">
      <td class="rd-td-pick"><input type="radio" name="rdPickRadio" class="rd-radio-input"
        value="${escapeHtmlAttr(r.id)}"${picked ? ' checked' : ''}
        aria-label="${escapeHtmlAttr(`Select ${r.name}, serial ${r.serialNumber}`)}"></td>
      <td class="rd-name">${escapeHtml(r.name)}</td>
      <td class="td-mono">${escapeHtml(r.serialNumber)}</td>
      <td class="td-mono td-muted">${escapeHtml(r.vin)}</td>
      <td>${escapeHtml(r.deviceType)}</td>
      <td class="td-muted">${escapeHtml(r.groupNames)}</td>
    </tr>`;
  }).join('');

  const table = document.getElementById('rdTable') as HTMLElement | null;
  if (table) {
    table.innerHTML = pageRows.length
      ? `<thead><tr>${thHtml}</tr></thead><tbody>${rowsHtml}</tbody>`
      : `<thead><tr>${thHtml}</tr></thead><tbody><tr><td colspan="6" class="rd-no-match">No devices match “${escapeHtml(rdSearch)}”.</td></tr></tbody>`;
  }

  const countEl = document.getElementById('rdCount');
  if (countEl) {
    countEl.textContent = `${sorted.length.toLocaleString()} Geotab device${sorted.length === 1 ? '' : 's'}`;
  }

  // Say why a vehicle someone expected isn't in the list.
  const exclEl = document.getElementById('rdExcludedNote');
  if (exclEl) {
    exclEl.textContent = rdExcludedNonGeotab
      ? `${rdExcludedNonGeotab.toLocaleString()} non-Geotab device${rdExcludedNonGeotab === 1 ? '' : 's'} hidden`
      : '';
    exclEl.classList.toggle('hidden', rdExcludedNonGeotab === 0);
  }

  renderRdPagination(currentPage, totalPages, sorted.length, pageStart, pageRows.length);
  renderRdSelection();
}

/**
 * Reflect the current pick in the action bar.
 *
 * Resolution is against the *filtered* set, not all rows: a device hidden by the
 * current search must not stay actionable, or you can reset a device you can't
 * see. When that happens the selection is kept (so clearing the search restores
 * it) but the button is disabled and the reason is stated.
 */
export function renderRdSelection(): void {
  const visible  = rdFilteredSorted();
  const inView   = visible.find((r) => r.id === rdSelectedId) ?? null;
  const anywhere = rdRows.find((r) => r.id === rdSelectedId) ?? null;

  const btn = document.getElementById('rdResetBtn') as HTMLButtonElement | null;
  if (btn) btn.disabled = !inView;

  const label = document.getElementById('rdSelectedLabel');
  if (!label) return;

  if (inView) {
    label.innerHTML = `Selected: <strong>${escapeHtml(inView.name)}</strong> `
                    + `<span class="td-mono">${escapeHtml(inView.serialNumber)}</span>`;
  } else if (anywhere) {
    label.innerHTML = `<span class="rd-sel-hidden">Selected device `
                    + `<strong>${escapeHtml(anywhere.name)}</strong> is hidden by the current search.</span> `
                    + `<button type="button" class="rd-inline-link" id="rdClearSearch">Clear search</button>`;
  } else {
    label.textContent = 'No device selected.';
  }
}

function renderRdPagination(
  currentPage: number, totalPages: number, totalRows: number,
  pageStart: number, shown: number,
): void {
  const el = document.getElementById('rdPagination');
  if (!el) return;

  const visible = new Set<number>([0, totalPages - 1]);
  for (let i = Math.max(0, currentPage - 2); i <= Math.min(totalPages - 1, currentPage + 2); i++) visible.add(i);

  const btns: string[] = [];
  let prev = -1;
  for (const p of [...visible].sort((a, b) => a - b)) {
    if (p > prev + 1) btns.push('<span class="la-pg-ellipsis">…</span>');
    btns.push(`<button class="la-pg-btn${p === currentPage ? ' la-pg-active' : ''}" data-page="${p}">${p + 1}</button>`);
    prev = p;
  }

  const pageEnd = totalRows === 0 ? 0 : pageStart + shown;
  const nav = totalPages <= 1 ? '' : `
    <button class="la-pg-btn" data-page="${currentPage - 1}" ${currentPage === 0 ? 'disabled' : ''}>‹</button>
    ${btns.join('')}
    <button class="la-pg-btn" data-page="${currentPage + 1}" ${currentPage >= totalPages - 1 ? 'disabled' : ''}>›</button>`;

  el.innerHTML = `${nav}<span class="la-pg-info">Showing ${totalRows === 0 ? 0 : pageStart + 1} to ${pageEnd} of ${totalRows.toLocaleString()} devices</span>`;
}
